import React from 'react';
import Select from 'react-select';

/**
 * StationSelect Component
 * A modern, accessible station selection component with create new station functionality
 * 
 * @param {Object} props
 * @param {string} props.value - Selected station ID
 * @param {Function} props.onChange - Callback when station is selected
 * @param {Array} props.stations - Array of station objects with _id and name
 * @param {Function} props.onCreateNew - Callback when "Create New Station" is clicked
 * @param {Object} props.customSelectStyles - Custom styles for react-select
 * @param {boolean} props.darkMode - Dark mode flag
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.label - Label text (default: "Station")
 * @param {string} props.placeholder - Placeholder text (default: "Select Station")
 * @param {string} props.createNewLabel - Label for create new option (default: "➕ Create New Station")
 * @param {string} props.createNewValue - Value for create new option (default: "CREATE_NEW")
 */
const StationSelect = ({
  value,
  onChange,
  stations = [],
  onCreateNew,
  customSelectStyles,
  darkMode = false,
  required = false,
  label = 'Station',
  placeholder = 'Select Station',
  createNewLabel = '➕ Create New Station',
  createNewValue = 'CREATE_NEW',
  className = ''
}) => {
  // Build options array with stations and create new option
  const options = [
    ...stations.map(station => ({ 
      value: station._id, 
      label: station.name,
      isStation: true
    })),
    { 
      value: createNewValue, 
      label: createNewLabel,
      isCreateNew: true
    }
  ];

  // Find selected station for display
  const selectedStation = stations.find(station => station._id === value);
  const selectedValue = selectedStation 
    ? { value: selectedStation._id, label: selectedStation.name, isStation: true } 
    : null;

  // Handle selection change
  const handleChange = (selectedOption) => {
    if (selectedOption?.value === createNewValue || selectedOption?.isCreateNew) {
      // Trigger create new station modal
      if (onCreateNew) {
        onCreateNew();
      }
    } else {
      // Update selected station
      if (onChange) {
        onChange(selectedOption?.value || '');
      }
    }
  };

  // Enhanced custom styles for react-select with better dark mode
  const enhancedSelectStyles = customSelectStyles || {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: darkMode ? '#3f4652' : '#ffffff',
      borderColor: state.isFocused 
        ? (darkMode ? '#10b981' : '#3b82f6')
        : (darkMode ? '#515a6b' : '#d1d5db'),
      borderWidth: '1px',
      borderRadius: '0.5rem',
      padding: '0.25rem 0.5rem',
      minHeight: '42px',
      boxShadow: state.isFocused 
        ? (darkMode ? '0 0 0 2px rgba(16, 185, 129, 0.15)' : '0 0 0 3px rgba(59, 130, 246, 0.1)')
        : 'none',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: darkMode ? '#10b981' : '#93c5fd',
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: darkMode ? '#2d3441' : '#ffffff',
      borderRadius: '0.5rem',
      border: `1px solid ${darkMode ? '#3f4652' : '#e5e7eb'}`,
      boxShadow: darkMode 
        ? '0 10px 30px -5px rgba(0, 0, 0, 0.6), 0 8px 16px -8px rgba(0, 0, 0, 0.4)'
        : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      marginTop: '0.5rem',
      overflow: 'hidden',
      zIndex: 100,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0.375rem',
      maxHeight: '280px',
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: darkMode ? '#1f2937' : '#f3f4f6',
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb': {
        background: darkMode ? '#4b5563' : '#d1d5db',
        borderRadius: '4px',
        '&:hover': {
          background: darkMode ? '#6b7280' : '#9ca3af',
        }
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? (darkMode ? '#10b981' : '#3b82f6')
        : state.isFocused
        ? (darkMode ? '#3f4652' : '#f3f4f6')
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : (darkMode ? '#e5e7eb' : '#1f2937'),
      cursor: 'pointer',
      borderRadius: '0.375rem',
      padding: '0.65rem 0.875rem',
      margin: '0.125rem 0',
      fontWeight: state.data.isCreateNew ? '600' : '500',
      fontSize: '0.875rem',
      transition: 'all 0.15s ease',
      '&:active': {
        backgroundColor: state.isSelected 
          ? (darkMode ? '#059669' : '#2563eb')
          : (darkMode ? '#4b5563' : '#e5e7eb'),
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: darkMode ? '#e5e7eb' : '#1f2937',
      fontWeight: '500',
      fontSize: '0.875rem',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: darkMode ? '#9ca3af' : '#6b7280',
      fontSize: '0.875rem',
      fontWeight: '400',
    }),
    input: (provided) => ({
      ...provided,
      color: darkMode ? '#e5e7eb' : '#1f2937',
      fontSize: '0.875rem',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: darkMode ? '#9ca3af' : '#6b7280',
      padding: '0 8px',
      transition: 'all 0.2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      '&:hover': {
        color: darkMode ? '#e5e7eb' : '#1f2937',
      }
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: darkMode ? '#9ca3af' : '#6b7280',
      padding: '0 8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: darkMode ? '#ef4444' : '#ef4444',
      }
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: darkMode ? '#515a6b' : '#d1d5db',
      margin: '8px 0',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px',
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: darkMode ? '#10b981' : '#3b82f6',
    }),
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <label className={`font-semibold mb-2 text-sm transition-colors duration-300 flex items-center gap-1.5 ${
        darkMode ? 'text-gray-200' : 'text-gray-700'
      }`}>
        <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        {label} {required && <span className="text-red-500 text-base">*</span>}
      </label>
      <div className="relative">
        {stations.length === 0 ? (
          // Empty state with prominent create button
          <div className={`relative rounded-lg border-2 border-dashed transition-all duration-300 ${
            darkMode 
              ? 'border-gray-600 bg-gray-800/30 hover:border-green-500 hover:bg-gray-800/50' 
              : 'border-gray-300 bg-gray-50 hover:border-green-500 hover:bg-green-50'
          }`}>
            <button
              type="button"
              onClick={onCreateNew}
              className={`w-full px-6 py-8 flex flex-col items-center justify-center gap-3 transition-all duration-300 group ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
                darkMode 
                  ? 'bg-green-600/20 group-hover:bg-green-600/30 group-hover:scale-110' 
                  : 'bg-green-100 group-hover:bg-green-200 group-hover:scale-110'
              }`}>
                <svg className={`w-7 h-7 transition-colors duration-300 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path>
                </svg>
              </div>
              <div className="text-center">
                <p className={`font-semibold text-base mb-1 transition-colors ${
                  darkMode ? 'text-green-400 group-hover:text-green-300' : 'text-green-600 group-hover:text-green-700'
                }`}>
                  Create First Station
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Add your first delivery location to get started
                </p>
              </div>
            </button>
          </div>
        ) : (
          // Normal state with select and button
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <Select
                name="station"
                value={selectedValue}
                onChange={handleChange}
                options={options}
                styles={enhancedSelectStyles}
                placeholder={placeholder}
                isSearchable
                isClearable
                noOptionsMessage={() => darkMode ? 'No stations found' : 'No stations found'}
                formatOptionLabel={(option) => (
                  <div className="flex items-center gap-2">
                    {option.isCreateNew ? (
                      <>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          darkMode ? 'bg-green-600/20' : 'bg-green-100'
                        }`}>
                          <svg className={`w-3.5 h-3.5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path>
                          </svg>
                        </div>
                        <span className={darkMode ? 'text-green-400' : 'text-green-600'}>{option.label}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        <span>{option.label}</span>
                      </>
                    )}
                  </div>
                )}
              />
            </div>
            <button
              type="button"
              onClick={onCreateNew}
              className={`px-3.5 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center min-w-[44px] group ${
                darkMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-900/20 hover:shadow-green-900/40' 
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'
              }`}
              title="Create New Station"
            >
              <svg className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path>
              </svg>
            </button>
          </div>
        )}
      </div>
      {stations.length > 0 && (
        <p className={`text-xs mt-1.5 flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {stations.length} station{stations.length !== 1 ? 's' : ''} available
        </p>
      )}
    </div>
  );
};

export default StationSelect;
